// Pure reducer helpers for EditOrderModal's services-array operations. Each
// operates on the canonical top-level `order.services` array and returns a NEW
// order object (immutable update) so React's identity re-render check fires.

// Append a service to `order.services`, initializing the array when missing
// (a GE order with no service lines yet has no `services` array).
export const addServiceLine = (order, service) => {
    const services = [...(order?.services || []), service];
    return { ...order, services };
};

// Effective unit count for a line: coerces a missing / 0 quantity to 1,
// mirroring the pricing algo so the displayed quantity equals what it bills.
// Exported so serviceCallback's display grouping uses the same coercion.
export const effectiveQty = (line) => Number(line?.quantity) || 1;

// Canonical key for a service code (trimmed + upper-cased) — the single definition
// of "same service line", shared by the index grouping here, the display grouping in
// serviceCallback, and the catalog lookups in serviceCatalog so the three can't drift.
// Codes are saved upper-cased but an ingested line's can arrive in any case, so a
// case-sensitive key would split `m101` and `M101` into separate groups that catalog
// validation already accepts as one service.
export const serviceCodeKey = (code) => `${code ?? ''}`.trim().toUpperCase();

// Indices of every line sharing a code key — the SERVICES tab edits duplicate
// rows as a single code-keyed group rather than by array index.
const groupIndices = (services, code) => {
    const target = serviceCodeKey(code);
    const out = [];
    services.forEach((line, idx) => {
        if (serviceCodeKey(line?.code) === target) out.push(idx);
    });
    return out;
};

// Step a code-group's total quantity by one unit (delta sign = up/down).
// Operates on the group's LAST line so the others' per-line metadata stays
// untouched; never reduces the group below one unit. No-op (same reference)
// when the code is absent or delta is 0.
export const adjustServiceGroupQuantity = (order, code, delta) => {
    const existing = order?.services || [];
    const idxs = groupIndices(existing, code);
    if (idxs.length === 0 || !delta) return order;

    const lastIdx = idxs[idxs.length - 1];
    const services = [...existing];
    const line = services[lastIdx];

    if (delta > 0) {
        services[lastIdx] = { ...line, quantity: effectiveQty(line) + 1 };
        return { ...order, services };
    }

    // delta < 0 — floor the whole group at a single unit.
    const total = idxs.reduce((acc, i) => acc + effectiveQty(existing[i]), 0);
    if (total <= 1) return order;
    if (effectiveQty(line) > 1) {
        services[lastIdx] = { ...line, quantity: effectiveQty(line) - 1 };
    } else {
        services.splice(lastIdx, 1);
    }
    return { ...order, services };
};

// Set service_time (minutes, or null) on every line sharing `code`'s canonical key.
// Returns the same reference when no line matches.
export const setServiceGroupServiceTime = (order, code, serviceTime) => {
    const existing = order?.services || [];
    const target = serviceCodeKey(code);
    let changed = false;
    const services = existing.map((line) => {
        if (serviceCodeKey(line?.code) !== target) return line;
        changed = true;
        return { ...line, service_time: serviceTime };
    });
    return changed ? { ...order, services } : order;
};

const isBlank = (value) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

// True when a description says something beyond its code. The parser stamps description = code
// when the source carries no human label, so an echo of the code is no label at all.
export const isServiceLabel = (description, code) =>
    !isBlank(description) && serviceCodeKey(description) !== serviceCodeKey(code);

// 'unknown' is the parser's couldn't-classify sentinel, so it reads as unset, like a blank.
const isUnsetType = (type) => isBlank(type) || `${type}`.trim().toLowerCase() === 'unknown';

// What a catalog entry fills on a line that predates it, per ingest's stamp rules (onward-functions
// lib/jobs/constants.js CATALOG_ATTRIBUTES): gaps only, and a recognized type is never replaced —
// the GE algo prices by it and pairs an install marker with its m_code. billable / require_confirm
// carry DB defaults, so a saved line never reads as a gap for them; they only move toward the
// entry's stricter value, which never re-bills a line an operator waived.
const catalogEntryFill = (line, entry) => ({
    ...(isUnsetType(line.type) && !isUnsetType(entry.type) ? { type: entry.type } : {}),
    ...(!isServiceLabel(line.description, line.code) && isServiceLabel(entry.description, entry.code)
        ? { description: entry.description }
        : {}),
    ...(isBlank(line.size) && !isBlank(entry.size) ? { size: entry.size } : {}),
    ...(isBlank(line.service_time) && Number.isFinite(entry.service_time) ? { service_time: entry.service_time } : {}),
    require_confirm: !!line.require_confirm || !!entry.require_confirm,
    billable: line.billable !== false && entry.billable !== false,
});

// Fill every line sharing `code`'s canonical key from a catalog entry (buildServiceOption's shape)
// — for a code just added to the catalog, which ingest never got to stamp onto these lines.
// Returns the same reference when no line matches.
export const applyCatalogEntryToServiceGroup = (order, code, entry) => {
    const existing = order?.services || [];
    const target = serviceCodeKey(code);
    let changed = false;
    const services = existing.map((line) => {
        if (serviceCodeKey(line?.code) !== target) return line;
        changed = true;
        return { ...line, ...catalogEntryFill(line, entry || {}) };
    });
    return changed ? { ...order, services } : order;
};

// Remove every line sharing `code`'s canonical key. Returns the same reference
// (no-op) when no line matches.
export const deleteServiceGroup = (order, code) => {
    const existing = order?.services || [];
    const target = serviceCodeKey(code);
    const remaining = existing.filter((line) => serviceCodeKey(line?.code) !== target);
    if (remaining.length === existing.length) return order;
    return { ...order, services: remaining };
};
